import React, { useEffect, useRef, useState } from 'react';
import { Settings, Clock, Wrench, AlertTriangle } from 'lucide-react';
import logodark from "../assets/images/logo-dark.png";

// --- Design Tokens (Adjusted for Target Image) ---
const designTokens = {
    colors: {
        primary: '#ffffff', // White
        accent: '#0d6efd', // Bootstrap Blue (Matches image)
        accentDark: '#0a58ca', // Darker blue (unused here but good practice)
        text: '#212529', // Standard Dark Grey/Black
        textSecondary: '#6c757d', // Standard Medium Grey
        background: '#f8f9fa',
        surface: '#ffffff',
        infoBoxBackground: '#f8f9fa',
        border: '#dee2e6',
        divider: '#e9ecef',
    },
    shadows: {
        // Subtle shadow like the image
        md: '0 4px 15px rgba(0, 0, 0, 0.07)',
    },
    spacing: { // Use consistent spacing units
        xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px', xxl: '48px',
    },
    borderRadius: {
        // Noticeable rounded corners like the image
        lg: '16px', // For the main card
        md: '12px', // For the inner info box
    },
    typography: {
        fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`,
        fontWeightNormal: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700, // For the main title
    }
};
// --- End Design Tokens ---

const MaintenancePage = () => {
    const [show, setShow] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        setTimeout(() => setShow(true), 100);
    }, []);

    return (
        <div className="maintenance-root">
            <style>{`
                .maintenance-root {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
                }
                .maintenance-bg-svg {
                    position: fixed;
                    top: 0; left: 0; width: 100vw; height: 100vh;
                    z-index: 0;
                    pointer-events: none;
                    overflow: hidden;
                }
                .glass-card {
                    background: rgba(255,255,255,0.75);
                    border: 2.5px solid #e3f0ff;
                    box-shadow: 0 12px 48px 0 rgba(13,110,253,0.22), 0 6px 24px rgba(0,0,0,0.10);
                    backdrop-filter: blur(12px) saturate(1.1);
                }
                .glass-box {
                    background: rgba(248,249,250,0.80);
                    backdrop-filter: blur(6px);
                    border: 1.5px solid #e9ecef;
                }
                .maintenance-card-split {
                    display: flex;
                    flex-direction: row;
                    border-radius: 32px;
                    max-width: 980px;
                    width: 96vw;
                    min-height: 520px;
                    overflow: hidden;
                    opacity: 0;
                    transform: translateY(40px) scale(0.98);
                    transition: box-shadow 0.3s cubic-bezier(.4,0,.2,1), transform 0.6s cubic-bezier(.4,0,.2,1), opacity 0.6s cubic-bezier(.4,0,.2,1);
                    position: relative;
                    z-index: 2;
                }
                .maintenance-card-bg-accent {
                    position: absolute;
                    top: -80px;
                    left: -120px;
                    width: 380px;
                    height: 380px;
                    background: radial-gradient(circle at 60% 40%, #e3f0ff 0%, #f8f9fa 80%);
                    z-index: 0;
                    filter: blur(2px);
                    opacity: 0.7;
                    pointer-events: none;
                }
                .maintenance-card-split.show {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                }
                .maintenance-left {
                    flex: 1.1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(120deg, #f8f9fa 60%, #e9ecef 100%);
                    padding: 70px 44px 70px 56px;
                    position: relative;
                    z-index: 1;
                }
                .maintenance-icon-stack {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    gap: 18px;
                    margin-bottom: 28px;
                }
                .maintenance-icon-bg {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #e3f0ff 0%, #b6d0fa 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 12px rgba(13,110,253,0.08);
                    margin-right: 0;
                }
                .maintenance-logo {
                    width: 200px;
                    height: 64px;
                    object-fit: contain;
                    border-radius: 14px;
                    margin-bottom: 22px;
                    box-shadow: 0 2px 12px rgba(13,110,253,0.10);
                    background: #fff;
                }
                .maintenance-hero-icon-bg {
                    width: 90px;
                    height: 90px;
                    border-radius: 50%;
                    background: linear-gradient(135deg,#d6eaff 0%,#b6d0fa 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 12px;
                    box-shadow: 0 4px 24px rgba(13,110,253,0.10);
                    animation: float 2.8s ease-in-out infinite alternate;
                }
                .maintenance-hero-icon {
                    filter: drop-shadow(0 2px 10px #0d6efd33);
                }
                .maintenance-icon-animate {
                    display: inline-block;
                    animation: rotate 2.5s linear infinite;
                    margin-bottom: 22px;
                }
                @keyframes rotate {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @keyframes float {
                    0% { transform: translateY(0); }
                    100% { transform: translateY(-14px); }
                }
                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }
                @keyframes pulse {
                    0% { box-shadow: 0 0 0 0 #fd7e1433; }
                    70% { box-shadow: 0 0 0 10px #fd7e1400; }
                    100% { box-shadow: 0 0 0 0 #fd7e1400; }
                }
                @keyframes spin {
                    100% { transform: rotate(360deg); }
                }
                .animate-bounce { animation: bounce 1.8s infinite; }
                .animate-pulse { animation: pulse 2.2s infinite; }
                .animate-spin { animation: spin 2.5s linear infinite; }
                
                .maintenance-title {
                    font-size: 2.1rem;
                    font-weight: 800;
                    color: #013e7d;
                    margin-bottom: 14px;
                    letter-spacing: -0.01em;
                    text-align: center;
                    text-shadow: 0 2px 12px rgba(13,110,253,0.09);
                }
                .maintenance-desc {
                    color: #6c757d;
                    font-size: 1.08rem;
                    margin-bottom: 0;
                    font-weight: 400;
                    text-align: center;
                }
                .maintenance-divider {
                    width: 1.5px;
                    background: linear-gradient(180deg, #e9ecef 0%, #dee2e6 100%);
                    margin: 32px 0;
                    border-radius: 2px;
                    align-self: stretch;
                    box-shadow: 0 0.5px 2px rgba(13,110,253,0.04);
                }
                .maintenance-right {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    padding: 70px 56px 70px 44px;
                    background: #fff;
                    position: relative;
                    z-index: 1;
                }
                .maintenance-info-icons {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 16px;
                }
                .maintenance-info-icon-bg {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #ffe9e3 0%, #ffe3e3 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 1px 6px rgba(253,110,13,0.08);
                }
                .maintenance-info-box {
                    border-radius: 18px;
                    box-shadow: 0 4px 18px rgba(13,110,253,0.07);
                    padding: 28px 24px 20px 24px;
                    margin-bottom: 28px;
                    position: relative;
                    overflow: hidden;
                    z-index: 1;
                }
                .maintenance-info-box::before {
                    content: '';
                    position: absolute;
                    top: 0; left: -120px;
                    height: 100%;
                    width: 120px;
                    background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(13,110,253,0.10) 40%, rgba(255,255,255,0) 100%);
                    animation: shimmer 2.2s infinite linear;
                    z-index: 0;
                }
                @keyframes shimmer {
                    0% { left: -120px; }
                    100% { left: 100%; }
                }
                .maintenance-info-title {
                    font-weight: 600;
                    font-size: 1.09rem;
                    color: #013e7d;
                    margin-bottom: 7px;
                }
                .maintenance-badge {
                    display: inline-block;
                    background: linear-gradient(90deg,#0d6efd 60%,#b6d0fa 100%);
                    color: #fff;
                    font-size: 1.03rem;
                    font-weight: 600;
                    border-radius: 16px;
                    padding: 5px 18px;
                    margin-bottom: 10px;
                    margin-top: 6px;
                    box-shadow: 0 2px 8px rgba(13,110,253,0.12);
                    letter-spacing: 0.01em;
                }
                .maintenance-info-desc {
                    color: #6c757d;
                    font-size: 1.08rem;
                    margin-bottom: 0;
                }
                .maintenance-progress-bar {
                    width: 100%;
                    height: 7px;
                    background: #e9ecef;
                    border-radius: 7px;
                    margin-top: 18px;
                    overflow: hidden;
                    position: relative;
                }
                .maintenance-progress-bar-inner {
                    width: 70%;
                    height: 100%;
                    background: linear-gradient(90deg, #0d6efd 60%, #b6d0fa 100%);
                    border-radius: 7px;
                    animation: progressBarAnim 3s ease-in-out infinite alternate;
                }
                @keyframes progressBarAnim {
                    0% { width: 40%; }
                    100% { width: 90%; }
                }
                .maintenance-time {
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    gap: 6px;
                    color: #0d6efd;
                    font-size: 1.07rem;
                    font-weight: 600;
                    margin-top: 24px;
                }
                .maintenance-footer {
                    margin-top: 50px;
                    color: #adb5bd;
                    font-size: 0.97rem;
                    letter-spacing: 0.01em;
                    text-align: left;
                }
                .maintenance-card-split:hover {
                    box-shadow: 0 16px 48px rgba(13,110,253,0.20), 0 8px 32px rgba(0,0,0,0.11);
                    border-color: #b6d0fa;
                }
                @media (max-width: 900px) {
                    .maintenance-card-split {
                        flex-direction: column;
                        min-width: 0;
                        min-height: 0;
                    }
                    .maintenance-divider {
                        display: none;
                    }
                    .maintenance-left, .maintenance-right {
                        padding: 32px 18px;
                    }
                    .maintenance-footer {
                        margin-top: 30px;
                        text-align: center;
                    }
                }
            `}</style>
            {/* Vibrant SVG/Gradient Background for depth */}
            <div className="maintenance-bg-svg">
                <svg width="100%" height="100%" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="900" cy="100" rx="340" ry="220" fill="#e3f0ff" fillOpacity="0.7"/>
                  <ellipse cx="300" cy="700" rx="320" ry="180" fill="#b6d0fa" fillOpacity="0.5"/>
                  <ellipse cx="1200" cy="600" rx="220" ry="120" fill="#ffd6c0" fillOpacity="0.38"/>
                </svg>
            </div>
            <div ref={cardRef} className={`maintenance-card-split glass-card${show ? ' show' : ''}`}>
                {/* Left Side: Logo, Hero Icon, Title, Desc */}
                <div className="maintenance-left">
                    <div className="maintenance-hero-icon-bg">
                        <Settings className="maintenance-hero-icon animate-bounce" color="#0d6efd" size={60} />
                    </div>
                    <img src={logodark} alt="M-SPHERE Logo" className="maintenance-logo" />
                    <div className="maintenance-title">Scheduled Maintenance</div>
                    <div className="maintenance-desc">
                        <span role="img" aria-label="tools" style={{fontSize: '1.4em', marginRight: 4}}>🛠️</span>
                        We’re making improvements to serve you better.<br />
                        <span style={{fontWeight: 500, color: '#0d6efd'}}>Please check back soon.</span>
                    </div>
                </div>
                <div className="maintenance-divider" />
                {/* Right Side: Info Box, Badge, Animated Icons, Time, Footer */}
                <div className="maintenance-right">
                    <div className="maintenance-info-icons">
                        <span className="maintenance-info-icon-bg animate-pulse">
                            <AlertTriangle color="#fd7e14" size={28} />
                        </span>
                        <span className="maintenance-info-icon-bg animate-spin">
                            <Clock color="#0d6efd" size={26} />
                        </span>
                    </div>
                    <div className="maintenance-info-box glass-box">
                        <div className="maintenance-info-title">System Upgrade in Progress</div>
                        <span className="maintenance-badge">Estimated Downtime: 30 mins</span>
                        <div className="maintenance-info-desc">Our platform is currently being updated for a better experience. We appreciate your patience.</div>
                        <div className="maintenance-progress-bar">
                            <div className="maintenance-progress-bar-inner" />
                        </div>
                    </div>
                    <div className="maintenance-time">
                        <Clock size={18} color="#0d6efd" style={{marginRight: 2}} />
                        {new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>
                    <div className="maintenance-footer">
                        &copy; {new Date().getFullYear()} Meinhardt Group &mdash; M-SPHERE Platform
                    </div>
                </div>
            </div>
        </div>
    );
}

    

export default MaintenancePage;